// Client-side search over the Help Center articles (constants/helpCenter).
//
// Small and forgiving: each query word scores against the question (best),
// the article's keywords, then the answer text; words of 4+ letters also match
// as prefixes ("reactivat" → reactivate, reactivation). Articles must match at
// least half the meaningful words, so a long ticket subject still finds
// something without every word having to hit.
import {HELP_ARTICLES} from '../constants/helpCenter';

const STOP = new Set(('a an and are as at be but by can cant could did do does doesnt dont for from get got had has have how i im ' +
    'if in into is isnt it its ive just me my no not of on or our please so that the their them then there this to too ' +
    'up us was wasnt we what when where which who why will with wont would you your yet hi hello thanks thank help').split(' '));

function words(text) {
    return (text || '').toLowerCase()
        .replace(/<[^>]+>/g, ' ')
        .replace(/&[a-z]+;/g, ' ')
        .replace(/['’]/g, '')
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
        .map(singular);
}

// "clinics" → "clinic", "fees" → "fee"; leaves "class", "access", "status"
function singular(w) {
    return w.length > 3 && w.endsWith('s') && !/(ss|us|is)$/.test(w) ? w.slice(0, -1) : w;
}

function terms(text) {
    return words(text).filter(w => w.length > 1 && !STOP.has(w));
}

// Pre-tokenized once per page load
let INDEX = null;
function index() {
    if (!INDEX) {
        INDEX = HELP_ARTICLES.map(article => ({
            article,
            q: new Set(words(article.q)),
            k: new Set(words(article.keywords)),
            a: new Set(words(article.a)),
        }));
    }
    return INDEX;
}

function hits(set, term) {
    if (set.has(term)) return 1;
    if (term.length < 4) return 0;
    for (const w of set) {
        if (w.startsWith(term) || (w.length >= 4 && term.startsWith(w))) return 0.8;
    }
    return 0;
}

/**
 * Articles matching free text, best first.
 * @param {string} text
 * @param {{limit?: number}} [opts]
 * @returns {Array<object>} help articles
 */
export function searchHelp(text, {limit = 20} = {}) {
    const query = [...new Set(terms(text))];
    if (!query.length) return [];
    const needed = Math.max(1, Math.ceil(query.length / 2));
    const phrase = (text || '').toLowerCase().trim();

    return index()
        .map(entry => {
            let score = 0;
            let matched = 0;
            for (const term of query) {
                const s = Math.max(hits(entry.q, term) * 3, hits(entry.k, term) * 2, hits(entry.a, term));
                if (s > 0) matched++;
                score += s;
            }
            if (phrase.length > 5 && entry.article.q.toLowerCase().includes(phrase)) score += 5;
            return {article: entry.article, score, matched};
        })
        .filter(r => r.matched >= needed)
        .sort((x, y) => y.score - x.score)
        .slice(0, limit)
        .map(r => r.article);
}
